// Signup.js - Customer signup page
// Two column layout — emotional messaging on left, form on right
// City field uses Google Places Autocomplete (cities only, US only).
// State auto-fills from the selected city.
// After Firestore write, checks SERVED_CITIES and routes accordingly:
//   in-area  → /verify-email (normal flow)
//   out-of-area → /out-of-area (saves outOfArea: true to Firestore)

import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

// Cities within our active service counties.
// Mahoning County OH + Trumbull County OH + Lawrence County PA.
// Keep in sync with Booking.js, ProviderSignup.js, and OutOfArea.js.
const SERVED_CITIES = [
  // Mahoning County, Ohio
  'youngstown', 'boardman', 'canfield', 'poland', 'struthers', 'campbell',
  'sebring', 'alliance', 'austintown', 'beaver', 'beloit', 'berlin center',
  'columbiana', 'craig beach', 'ellsworth', 'goshen', 'jackson', 'lake milton',
  'lowellville', 'new middletown', 'north lima', 'springfield',
  // Trumbull County, Ohio
  'warren', 'niles', 'cortland', 'girard', 'hubbard', 'brookfield',
  'newton falls', 'lordstown', 'bazetta', 'bloomfield', 'bristolville',
  'champion', 'farmington', 'fowler', 'kinsman', 'liberty', 'leavittsburg',
  'mcdonald', 'masury', 'mineral ridge', 'orwell', 'southington', 'vienna',
  'weathersfield',
  // Lawrence County, Pennsylvania
  'new castle', 'ellwood city', 'wampum', 'volant', 'enon valley',
  'edinburg', 'bessemer', 'neshannock', 'shenango', 'hickory',
  'north sewickley', 'pulaski', 'plain grove',
];

function Signup() {
  const navigate = useNavigate();

  // Form field state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  // City/state — city is set by Places autocomplete, state is auto-filled from it
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Refs for Google Places — we attach autocomplete to the city input DOM node
  const cityInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // ===== GOOGLE PLACES AUTOCOMPLETE =====
  // Loaded dynamically so the API key never appears in index.html
  useEffect(() => {
    const initAutocomplete = () => {
      if (!cityInputRef.current || !window.google) return;

      // Restrict to city-level results within the US only
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        cityInputRef.current,
        {
          types: ['(cities)'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components'],
        }
      );

      // When a city is chosen from the dropdown, extract city and state
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.address_components) return;

        let cityName = '';
        let stateName = '';

        place.address_components.forEach(component => {
          // locality = city name
          if (component.types.includes('locality')) {
            cityName = component.long_name;
          }
          // administrative_area_level_1 = state abbreviation (e.g. "OH")
          if (component.types.includes('administrative_area_level_1')) {
            stateName = component.short_name;
          }
        });

        // Store lowercase for Firestore and SERVED_CITIES comparison
        setCity(cityName.toLowerCase().trim());
        setState(stateName.toLowerCase().trim());

        // Keep the visible input showing just the city name
        if (cityInputRef.current) {
          cityInputRef.current.value = cityName;
        }

        // Mark as touched so the field validation reacts
        setTouched(prev => ({ ...prev, city: true }));
      });
    };

    const loadGooglePlaces = () => {
      // Already loaded — init immediately
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
        return;
      }
      // Script tag already injected (e.g. by Booking.js) — attach to its onload
      const existingScript = document.getElementById('google-places-script');
      if (existingScript) {
        existingScript.onload = initAutocomplete;
        return;
      }
      // First load — inject the script
      const script = document.createElement('script');
      script.id = 'google-places-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    };

    loadGooglePlaces();

    // Clear autocomplete listeners on unmount to prevent memory leaks
    return () => {
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // ===== FIELD VALIDATION =====
  // Password strength checks — all four must pass before signup is allowed
  const pwChecks = (pw) => ({
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[!@#$%^&*()\-_=+[\]{}|;:,.<>?]/.test(pw),
  });

  // Returns an error string for a field, or null if valid
  const getFieldError = (name) => {
    switch (name) {
      case 'firstName': return !firstName.trim() ? 'First name is required' : null;
      case 'lastName':  return !lastName.trim()  ? 'Last name is required'  : null;
      case 'email': {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const domain = email.split('@')[1] || '';
        if (!email.trim())          return 'Email is required';
        if (!emailRegex.test(email)) return 'Enter a valid email address';
        if (domain.length > 253)     return 'Enter a valid email address';
        return null;
      }
      // City is set by autocomplete — empty means user hasn't selected from dropdown
      case 'city': return !city ? 'Please select your city from the dropdown' : null;
      case 'password': {
        if (!password) return 'Password is required';
        const checks = pwChecks(password);
        if (!checks.length)   return 'At least 8 characters required';
        if (!checks.upper)    return 'Include at least one uppercase letter';
        if (!checks.number)   return 'Include at least one number';
        if (!checks.special)  return 'Include at least one special character';
        return null;
      }
      case 'confirmPassword':
        if (!confirmPassword)            return 'Please confirm your password';
        if (confirmPassword !== password) return 'Passwords do not match';
        return null;
      default: return null;
    }
  };

  const isValid   = (name) => touched[name] && !getFieldError(name);
  const isInvalid = (name) => touched[name] &&  !!getFieldError(name);
  const touch     = (name) => setTouched(prev => ({ ...prev, [name]: true }));

  // ===== SIGNUP HANDLER =====
  const handleSignup = async () => {
    if (!firstName || !lastName || !email || !city || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const checks = pwChecks(password);
    if (!Object.values(checks).every(Boolean)) {
      setError('Password does not meet the strength requirements below');
      return;
    }
    // Ensure the user actually chose a city from the Places dropdown
    if (!city) {
      setError('Please select your city from the autocomplete dropdown');
      return;
    }

    let user = null;

    try {
      setLoading(true);
      setError('');

      // Step 1 — Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      user = userCredential.user;

      // Step 2 — Determine service area status before the Firestore write
      const isServed = SERVED_CITIES.includes(city.toLowerCase().trim());

      // Step 3 — Write profile to Firestore (with timeout guard against silent hangs)
      // city and state stored lowercase for consistent querying and matching
      const writePromise = setDoc(doc(db, 'users', user.uid), {
        firstName,
        lastName,
        email,
        city:               city.toLowerCase().trim(),
        state:              state.toLowerCase().trim(),
        role:               'customer',
        outOfArea:          !isServed,
        marketingConsent:   marketingConsent,
        // Onboarding (photo + gravesite) is guided by the dashboard checklist,
        // not a separate /onboarding route — mark complete so login never redirects.
        onboardingComplete: true,
        createdAt:          new Date(),
      });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Firestore write timed out')), 10000)
      );

      await Promise.race([writePromise, timeoutPromise]);

      // Step 4 — Route based on service area
      if (!isServed) {
        // Out-of-area: skip email verification and show the expansion request page
        navigate('/out-of-area');
      } else {
        // In-area: send verification email then gate on /verify-email.
        // Terms acknowledgment happens after onboarding, not at signup.
        try {
          await sendEmailVerification(user);
        } catch (verifyErr) {
          console.warn('Could not send verification email:', verifyErr.message);
        }
        navigate('/verify-email');
      }

    } catch (err) {
      // If Auth succeeded but Firestore failed, roll back the Auth account
      // so the user can try again without getting "email already in use"
      if (user) {
        try { await user.delete(); } catch { /* ignore — account may already be gone */ }
      }
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else {
        setError('Something went wrong. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="auth-split-page">

      {/* ========== LEFT PANEL — Emotional messaging ========== */}
      <div className="auth-split-left">
        <a href="/" className="auth-split-logo">Evermark Memorial</a>
        <div className="auth-split-message">
          <h2>They deserve to be remembered.</h2>
          <p>
            Join thousands of families who trust Evermark Memorial to care
            for their loved ones' graves — no matter the distance.
          </p>
        </div>
        <div className="auth-trust-points">
          <div className="auth-trust-item">
            <span className="auth-trust-icon">🛡️</span>
            <span>Background checked caretakers</span>
          </div>
          <div className="auth-trust-item">
            <span className="auth-trust-icon">📸</span>
            <span>Before & after photo proof</span>
          </div>
          <div className="auth-trust-item">
            <span className="auth-trust-icon">💚</span>
            <span>Caring, respectful service</span>
          </div>
          <div className="auth-trust-item">
            <span className="auth-trust-icon">🔒</span>
            <span>Secure payments</span>
          </div>
        </div>
        <div className="auth-split-quote">
          <p>"The peace of knowing someone is there,<br />even when you can't be."</p>
        </div>
      </div>

      {/* ========== RIGHT PANEL — Signup form ========== */}
      <div className="auth-split-right">
        <div className="auth-card">
          <h2>Create your account</h2>
          <p className="auth-subtitle">Start honoring your loved ones today</p>

          {error && <div className="auth-error">{error}</div>}

          {/* ===== FIRST AND LAST NAME ===== */}
          <div className="auth-row">
            <div className={`auth-field ${isInvalid('firstName') ? 'field-error' : ''} ${isValid('firstName') ? 'field-valid' : ''}`}>
              <label>First Name</label>
              <input
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={() => touch('firstName')}
                maxLength={100}
              />
              {isValid('firstName')   && <span className="field-check">✓</span>}
              {isInvalid('firstName') && <span className="field-error-msg">{getFieldError('firstName')}</span>}
            </div>
            <div className={`auth-field ${isInvalid('lastName') ? 'field-error' : ''} ${isValid('lastName') ? 'field-valid' : ''}`}>
              <label>Last Name</label>
              <input
                type="text"
                placeholder="Smith"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={() => touch('lastName')}
                maxLength={100}
              />
              {isValid('lastName')   && <span className="field-check">✓</span>}
              {isInvalid('lastName') && <span className="field-error-msg">{getFieldError('lastName')}</span>}
            </div>
          </div>

          {/* ===== EMAIL ===== */}
          <div className={`auth-field ${isInvalid('email') ? 'field-error' : ''} ${isValid('email') ? 'field-valid' : ''}`}>
            <label>Email Address</label>
            <input
              type="email"
              placeholder="john@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => touch('email')}
              maxLength={254}
            />
            {isValid('email')   && <span className="field-check">✓</span>}
            {isInvalid('email') && <span className="field-error-msg">{getFieldError('email')}</span>}
          </div>

          {/* ===== CITY WITH GOOGLE PLACES AUTOCOMPLETE =====
              Uncontrolled input — Google Places writes directly to the DOM node.
              The city and state React state variables are set via place_changed. */}
          <div className="auth-row">
            <div className={`auth-field ${isInvalid('city') ? 'field-error' : ''} ${isValid('city') ? 'field-valid' : ''}`}>
              <label>Your City</label>
              <input
                ref={cityInputRef}
                type="text"
                placeholder="Start typing your city..."
                defaultValue=""
                className="auth-input-autocomplete"
                onBlur={() => touch('city')}
              />
              <p className="autocomplete-hint">Select from the dropdown</p>
              {isValid('city')   && <span className="field-check">✓</span>}
              {isInvalid('city') && <span className="field-error-msg">{getFieldError('city')}</span>}
            </div>

            {/* State auto-fills when city is selected — read-only */}
            <div className="auth-field">
              <label>State</label>
              <input
                type="text"
                value={state ? state.toUpperCase() : ''}
                readOnly
                placeholder="Auto-filled"
                className="auth-input-readonly"
              />
            </div>
          </div>

          {/* ===== PASSWORD ===== */}
          <div className={`auth-field ${isInvalid('password') ? 'field-error' : ''} ${isValid('password') ? 'field-valid' : ''}`}>
            <label>Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => touch('password')}
                maxLength={128}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(prev => !prev)}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {isInvalid('password') && <span className="field-error-msg">{getFieldError('password')}</span>}
            {/* Password strength indicator */}
            {password && (
              <div className="pw-strength">
                {[
                  { key: 'length',  label: 'At least 8 characters' },
                  { key: 'upper',   label: 'One uppercase letter' },
                  { key: 'number',  label: 'One number' },
                  { key: 'special', label: 'One special character (!@#$…)' },
                ].map(({ key, label }) => {
                  const pass = pwChecks(password)[key];
                  return (
                    <div key={key} className={`pw-check ${pass ? 'pw-check-pass' : 'pw-check-fail'}`}>
                      <span>{pass ? '✓' : '✗'}</span> {label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ===== CONFIRM PASSWORD ===== */}
          <div className={`auth-field ${isInvalid('confirmPassword') ? 'field-error' : ''} ${isValid('confirmPassword') ? 'field-valid' : ''}`}>
            <label>Confirm Password</label>
            <input
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => touch('confirmPassword')}
              maxLength={128}
            />
            {isValid('confirmPassword')   && <span className="field-check">✓</span>}
            {isInvalid('confirmPassword') && <span className="field-error-msg">{getFieldError('confirmPassword')}</span>}
          </div>

          {/* ========== MARKETING CONSENT ==========
              Optional — customer can opt in to having visit photos used
              for testimonials or social media. Stored in Firestore. */}
          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '16px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              style={{ marginTop: '2px', flexShrink: 0 }}
            />
            <span style={{ fontSize: '13px', color: '#555', lineHeight: '1.5' }}>
              I allow Evermark Memorial to use my visit photos and experience for testimonials or social media (optional — you can change this later in your profile)
            </span>
          </label>

          {/* Signup button */}
          <button
            className="btn-primary btn-full"
            onClick={handleSignup}
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          {/* Terms agreement happens after email verification and onboarding —
              not at signup time. */}
          <p className="auth-switch">
            Already have an account? <a href="/login">Log in</a>
          </p>
        </div>
      </div>

    </div>
  );
}

export default Signup;
